'use client';

import * as React from 'react';
import {
  Printer,
  Download,
  Filter,
  Loader2,
  BarChart3,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { PageHeader } from '../layout/page-header';
import { Separator } from '../ui/separator';
import { ExportMenu, type ExportFormat } from '../composite/export-menu';

export interface ReportPageProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  /** Filter components rendered in the filter bar */
  filters?: React.ReactNode;
  /** Whether filters are visible */
  showFilters?: boolean;
  onToggleFilters?: () => void;
  /** Chart component to render */
  chart?: React.ReactNode;
  /** Table component to render below chart */
  table?: React.ReactNode;
  /** Export handler */
  onExport?: (format: ExportFormat) => void;
  /** Print handler */
  onPrint?: () => void;
  /** Additional header actions */
  actions?: React.ReactNode;
  /** Loading state */
  isLoading?: boolean;
  /** Show empty state */
  isEmpty?: boolean;
  emptyMessage?: string;
  className?: string;
}

const ReportPage = React.forwardRef<HTMLDivElement, ReportPageProps>(
  (
    {
      title,
      subtitle,
      icon,
      filters,
      showFilters = true,
      onToggleFilters,
      chart,
      table,
      onExport,
      onPrint,
      actions,
      isLoading = false,
      isEmpty = false,
      emptyMessage = 'No data available for this report',
      className,
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cn('flex flex-col h-full bg-[var(--color-background)]', className)}
      >
        {/* Page Header */}
        <PageHeader
          title={title}
          subtitle={subtitle}
          icon={icon || <BarChart3 className="h-6 w-6" />}
          actions={
            <div className="flex items-center gap-2">
              {actions}
              {onExport && (
                <ExportMenu
                  onExport={onExport}
                  label="Export"
                  variant="outline"
                  size="default"
                />
              )}
              {onPrint && (
                <Button
                  variant="outline"
                  size="default"
                  onClick={onPrint}
                  className="gap-2"
                >
                  <Printer className="h-4 w-4" />
                  <span className="hidden sm:inline">Print</span>
                </Button>
              )}
            </div>
          }
        />

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          {/* Filters Section */}
          {showFilters && filters && (
            <>
              <div className="border-b border-[var(--color-border)] bg-[var(--color-card)]">
                <div className="px-6 py-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Filter className="h-4 w-4 text-[var(--color-muted-foreground)]" />
                      <h2 className="font-semibold text-[var(--color-foreground)]">
                        Filters
                      </h2>
                    </div>
                    {onToggleFilters && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={onToggleFilters}
                        className="text-xs"
                      >
                        {showFilters ? 'Hide' : 'Show'}
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {filters}
                  </div>
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-16 px-6">
              <Loader2 className="h-8 w-8 animate-spin text-[var(--color-primary)] mb-4" />
              <p className="text-sm text-[var(--color-muted-foreground)]">
                Loading report data...
              </p>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && isEmpty && (
            <div className="flex flex-col items-center justify-center py-16 px-6">
              <BarChart3 className="h-12 w-12 text-[var(--color-muted-foreground)] mb-4 opacity-50" />
              <h3 className="text-lg font-semibold text-[var(--color-foreground)] mb-2">
                No Data
              </h3>
              <p className="text-sm text-[var(--color-muted-foreground)] text-center max-w-sm">
                {emptyMessage}
              </p>
            </div>
          )}

          {/* Report Content */}
          {!isLoading && !isEmpty && (
            <>
              {/* Chart Section */}
              {chart && (
                <>
                  <div className="p-6">
                    <Card className="border border-[var(--color-border)]">
                      <CardHeader>
                        <CardTitle className="text-lg">
                          Report Visualization
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="min-h-96">
                        {chart}
                      </CardContent>
                    </Card>
                  </div>
                  {table && <Separator />}
                </>
              )}

              {/* Table Section */}
              {table && (
                <div className="p-6">
                  <Card className="border border-[var(--color-border)]">
                    <CardHeader>
                      <CardTitle className="text-lg">
                        Report Data
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {table}
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  },
);

ReportPage.displayName = 'ReportPage';

export { ReportPage };
